import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import LocalStorageService from "../../../../utils/localStorageServices";
import SOURCES from "../../../../utils/constants";
import useDebounce from "../../../../utils/customHooks";
import { QUERY_KEYS, QUERY_OPTIONS } from "../../../../lib/query-constants";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import CommonCard from "../../../global/card";
import CommonSkeletonLoader from "../../../global/loading";
import CommonPagination from "../../../global/pagination";
import AddUser from "../../modules/addUser";
import EnhancedUserList from "./enhanced-users-list";
import { setCurrentUser } from "../../../../redux/actions";
import AdvanceSearchUser from "../../modules/advanceSearchUser";

const EnhancedUsers = ({ showFormInitial = false }) => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
  const limitFromQuery = parseInt(
    searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
    24
  );
  const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

  const [pagination, setPagination] = useState({
    offset: offsetFromQuery,
    limit: limitFromQuery,
    page: pageFromQuery,
  });

  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [advanceSearch, setAdvanceSearch] = useState({
    advance_search_enabled:
      SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
    plan: "",
    status: "",
    trial: "",
    userStatus: "",
    leadSource: "",
    verified: "",
  });

  const [showForm, setShowForm] = useState(showFormInitial);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const [showDetail, setshowDeatail] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);
  const [deleteId, setDeleteId] = useState();
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [usersViewType, setUsersViewType] = useState(() => {
    return localStorage.getItem("usersViewType") || "GRID";
  });

  const localStorageServices = LocalStorageService.getService();

  const dispatch = useDispatch();
  useEffect(() => {
    setAdvanceSearch((prev) => ({
      ...prev,
      advance_search_enabled:
        SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
    }));
    setShowAdvanceSearch(false);
  }, [searchInput]);

  const [userInfo, setUserInfo] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    plan: "",
    coupon: "",
  });

  const params = useMemo(
    () => ({
      offset: pagination.offset,
      limit: pagination.limit,
      sortBy,
      sortOrder,
    }),
    [pagination.offset, pagination.limit, sortBy, sortOrder]
  );

  const advanceSearchParams = useMemo(
    () => ({
      advance_search: advanceSearch.advance_search_enabled,
      plan: advanceSearch.plan,
      status: advanceSearch.verified,
      trial: advanceSearch.trial,
      userStatus: advanceSearch.userStatus,
      leadSource: advanceSearch.leadSource,
      verified: advanceSearch.verified,
    }),
    [pagination.offset, pagination.limit, sortBy, sortOrder, advanceSearch]
  );

  const queryKey = QUERY_KEYS.users.lists(params);

  const {
    data,
    isLoading: usersGenericLoader,
    refetch: refetchUsers,
  } = useGenericQuery({
    endpoint: "admin/users",
    queryKey,
    queryParams: params,
    onError: () => { },
    onSuccess: () => { },
  });

  const {
    data: searchUsersData,
    isInitialLoading: searchUsersGenericLoader,
    refetch: refetchSearchUsers,
  } = useGenericQuery({
    endpoint: "admin/users/search",
    queryKey: QUERY_KEYS.users.lists({ q: debouncedSearchTerm }),
    queryParams: { q: debouncedSearchTerm },
    onError: () => { },
    onSuccess: () => { },
    enabled: !!debouncedSearchTerm,
  });

  const {
    data: advanceSearchUsersData,
    isInitialLoading: advanceSearchUsersGenericLoader,
    refetch: refetchAdvanceSearchUsers,
  } = useGenericQuery({
    endpoint: "admin/users/search-advance",
    queryKey: QUERY_KEYS.users.lists({ ...advanceSearchParams }),
    queryParams: { ...advanceSearchParams },
    onError: () => { },
    onSuccess: () => { },
    enabled:
      advanceSearch.advance_search_enabled ===
      SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_FILTER &&
      Object.values(advanceSearchParams).some(
        (val) => val !== "" && val !== null && val !== undefined
      ),
  });

  const { data: paymentPlans } = useGenericQuery({
    endpoint: "payment/plans?includePrivatePlans=y",
    queryKey: QUERY_KEYS.paymentPlans.all,
    onError: () => { },
    onSuccess: () => { },
    ...QUERY_OPTIONS,
  });

  const usersData = () => {
    if (
      searchUsersData &&
      searchInput &&
      Array.isArray(searchUsersData?.users)
    ) {
      return searchUsersData.users;
    } else if (
      advanceSearchUsersData &&
      advanceSearch?.advance_search_enabled !== "n" &&
      Array.isArray(advanceSearchUsersData?.users)
    ) {
      return advanceSearchUsersData.users;
    } else if (Array.isArray(data?.users)) {
      return data.users;
    }

    return [];
  };

  const isAdvanceSearchActive =
    advanceSearch.advance_search_enabled ===
    SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_FILTER;

  const isSearchActive = Boolean(
    debouncedSearchTerm && debouncedSearchTerm.length >= 2
  );

  const refetchAllUsers = useCallback(() => {
    if (isAdvanceSearchActive) {
      return refetchAdvanceSearchUsers();
    }

    if (isSearchActive) {
      return refetchSearchUsers();
    }

    return refetchUsers();
  }, [
    isAdvanceSearchActive,
    isSearchActive,
    refetchAdvanceSearchUsers,
    refetchSearchUsers,
    refetchUsers,
  ]);

  const { mutateAsync: accountStatus, isSuccess: processSuccess } =
    useGenericMutation({
      endpoint: `admin/status`,
      onSuccess: () => {
        refetchAllUsers();
      },
    });

  const { mutateAsync: loginUser } = useGenericMutation({
    endpoint: `admin/login-as-user`,
  });

  const { mutateAsync: removeUser, isSuccess: removeSuccess } =
    useGenericMutation({
      endpoint: `admin/remove-user`,
      onSuccess: () => {
        refetchAllUsers();
      },
    });

  const deleteUser = async (id) => {
    await removeUser({ id });
  };

  const loginAsUser = async (email) => {
    const userData = await loginUser({ email });
    console.log(userData)
    localStorageServices.clearToken();
    localStorageServices.setCurrentUser(userData?.data);
    dispatch(setCurrentUser(userData));
    window.location.href = "/dashboard";
  };

  const finalizedUserData = useMemo(
    () => usersData(),
    [data, searchUsersData, advanceSearchUsersData]
  );

  const isLoading =
    !!usersGenericLoader ||
    !!searchUsersGenericLoader ||
    !!advanceSearchUsersGenericLoader;

  return (
    <CommonCard
      isAdvanceSearch={{
        show: true,
        children: <AdvanceSearchUser
          isOpen={showAdvanceSearch}
          advanceSearch={advanceSearch}
          setShowAdvanceSearch={setShowAdvanceSearch}
          setAdvanceSearch={setAdvanceSearch}
          getAdvanceSearchData={refetchAdvanceSearchUsers}
          paymentPlans={paymentPlans}
        />
      }}
      isLoading={{
        show: isLoading,
        loaderCmp: (
          <CommonSkeletonLoader
            rows={9}
            type={usersViewType === "GRID" ? "card" : "table"}
            tableLength={7}
          />
        ),
      }}
      isSearch={{
        show: true,
        searchInput: searchInput,
        placeholder: "Search users...",
        setSearchInput: setSearchInput,
      }}
      isListGridView={{
        show: true,
        viewFor: "usersViewType",
        viewType: usersViewType,
        setViewType: setUsersViewType,
      }}
      isCreateButton={{
        show: true,
        text: "Add User",
        onclick: () => setShowForm(true),
      }}
    >
      <EnhancedUserList
        userList={finalizedUserData}
        isLoading={isLoading}
        leadDetail={leadDetail}
        setshowDeatail={setshowDeatail}
        showDetail={showDetail}
        setLeadDetail={setLeadDetail}
        getUsers={refetchAllUsers}
        searchInput={searchInput}
        accountStatus={accountStatus}
        loginAsUser={loginAsUser}
        deleteUser={deleteUser}
        removeSuccess={removeSuccess}
        processSuccess={processSuccess}
        setDeleteId={setDeleteId}
        deleteId={deleteId}
        setSortBy={setSortBy}
        sortBy={sortBy}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        usersViewType={usersViewType}
        paymentPlans={paymentPlans}
        userInfo={userInfo}
        setUserInfo={setUserInfo}
        onMutationSuccess={refetchAllUsers}
      />

      {Array.isArray(finalizedUserData) &&
        finalizedUserData.length > 0 &&
        !searchInput && (
          <CommonPagination
            currentPage={pagination.page}
            defaultLimit={pagination.limit}
            totalRecords={data?.count}
            setCurrentPage={setPagination}
            activePageRecords={
              Array.isArray(finalizedUserData)
                ? finalizedUserData?.length
                : 0
            }
            paginationFor="Users"
            className="mt-4"
          />
        )}

      <AddUser
        paymentPlans={paymentPlans}
        showForm={showForm}
        setShowForm={setShowForm}
        getUsers={refetchAllUsers}
      />
    </CommonCard>
  );
};

export default EnhancedUsers;